(() => {
  if (window.__elagiliNativeCardCartLoaded) return;
  window.__elagiliNativeCardCartLoaded = true;

  let cartQuantities = new Map();
  let cartLineKeys = new Map();
  const busyVariants = new Set();

  const rebuildCartMaps = (cart) => {
    cartQuantities = new Map();
    cartLineKeys = new Map();

    for (const item of cart?.items || []) {
      const variantId = String(item.variant_id);
      cartQuantities.set(variantId, (cartQuantities.get(variantId) || 0) + Number(item.quantity || 0));
      if (!cartLineKeys.has(variantId) && item.key) cartLineKeys.set(variantId, item.key);
    }
  };

  const fetchCart = async () => {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });

    if (!response.ok) throw new Error(`Cart read failed: HTTP ${response.status}`);
    const cart = await response.json();
    rebuildCartMaps(cart);
    return cart;
  };

  const emitCartUpdate = (cart, variantId) => {
    document.dispatchEvent(
      new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          sourceId: 'elagili-native-card-cart',
          data: {
            source: 'elagili-native-card-cart',
            variantId,
            itemCount: cartQuantities.get(String(variantId)) || 0,
          },
          resource: cart,
        },
      })
    );
  };

  const getRows = (variantId) =>
    document.querySelectorAll(`.elagili-card-buy-row[data-variant-id="${CSS.escape(String(variantId))}"]`);

  const renderRow = (row) => {
    const variantId = String(row.dataset.variantId || '');
    if (!variantId) return;

    const available = row.dataset.available === 'true';
    const qty = cartQuantities.get(variantId) || 0;
    const busy = busyVariants.has(variantId);
    const control = row.querySelector('.elagili-card-control');
    if (!control) return;

    if (!available) {
      control.innerHTML = '<span class="elagili-card-soldout">Sold out</span>';
      return;
    }

    if (qty > 0) {
      control.innerHTML = `
        <div class="elagili-card-qty">
          <button type="button" class="elagili-card-minus" aria-label="Decrease quantity" ${busy ? 'disabled' : ''}>−</button>
          <span class="elagili-card-count">${qty}</span>
          <button type="button" class="elagili-card-plus" aria-label="Increase quantity" ${busy ? 'disabled' : ''}>+</button>
        </div>
      `;
      return;
    }

    control.innerHTML = `<button type="button" class="elagili-card-add" ${busy ? 'disabled' : ''}>Add</button>`;
  };

  const renderVariant = (variantId) => {
    getRows(variantId).forEach(renderRow);
  };

  const renderAll = () => {
    document.querySelectorAll('.elagili-card-buy-row[data-variant-id]').forEach(renderRow);
  };

  const addVariant = async (variantId) => {
    variantId = String(variantId);
    if (!variantId || busyVariants.has(variantId)) return;

    busyVariants.add(variantId);
    renderVariant(variantId);

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) throw new Error(`Add failed: HTTP ${response.status}`);

      const cart = await fetchCart();
      emitCartUpdate(cart, variantId);
    } catch (error) {
      console.error('Elagili native card add failed', error);
      try {
        await fetchCart();
      } catch (_) {}
    } finally {
      busyVariants.delete(variantId);
      renderVariant(variantId);
    }
  };

  const changeVariantQuantity = async (variantId, quantity) => {
    variantId = String(variantId);
    quantity = Math.max(0, Number(quantity) || 0);
    if (!variantId || busyVariants.has(variantId)) return;

    busyVariants.add(variantId);
    renderVariant(variantId);

    try {
      if (!cartLineKeys.has(variantId)) await fetchCart();
      const lineKey = cartLineKeys.get(variantId);
      if (!lineKey) throw new Error('Cart line key not found');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ id: lineKey, quantity }),
      });

      if (!response.ok) throw new Error(`Quantity change failed: HTTP ${response.status}`);

      const cart = await response.json();
      rebuildCartMaps(cart);
      emitCartUpdate(cart, variantId);
    } catch (error) {
      console.error('Elagili native card quantity update failed', error);
      try {
        await fetchCart();
      } catch (_) {}
    } finally {
      busyVariants.delete(variantId);
      renderVariant(variantId);
    }
  };

  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest?.('.elagili-card-add, .elagili-card-plus, .elagili-card-minus');
      if (!button) return;

      const row = button.closest('.elagili-card-buy-row');
      const variantId = row?.dataset.variantId;
      if (!variantId) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const current = cartQuantities.get(String(variantId)) || 0;

      if (button.classList.contains('elagili-card-add')) {
        addVariant(variantId);
      } else if (button.classList.contains('elagili-card-plus')) {
        changeVariantQuantity(variantId, current + 1);
      } else if (button.classList.contains('elagili-card-minus')) {
        changeVariantQuantity(variantId, current - 1);
      }
    },
    true
  );

  document.addEventListener('cart:update', (event) => {
    if (event.detail?.data?.source === 'elagili-native-card-cart') return;
    fetchCart().then(renderAll).catch(() => {});
  });

  document.addEventListener('shopify:section:load', () => {
    fetchCart().then(renderAll).catch(() => {});
  });

  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    fetchCart().then(renderAll).catch(() => {});
  });

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => fetchCart().then(renderAll).catch(() => {}),
      { once: true }
    );
  } else {
    fetchCart().then(renderAll).catch(() => {});
  }
})();

(() => {
  if (window.__elagiliBackInStockLoaded) return;
  window.__elagiliBackInStockLoaded = true;

  const styleId = 'elagili-back-in-stock-styles';

  const injectStyles = () => {
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .elagili-notify-btn{border:1px solid #354557;border-radius:999px;background:#fff;color:#354557;min-height:36px;padding:7px 13px;font:inherit;font-weight:600;line-height:1;cursor:pointer;white-space:nowrap;position:relative;z-index:45}
      .elagili-notify-btn:hover{background:#354557;color:#fff}
      .elagili-notify-btn--product{width:100%;min-height:46px;margin:10px 0 12px}
      .elagili-notify-overlay{position:fixed;z-index:150;inset:0;background:rgba(20,27,34,.42);display:none;align-items:center;justify-content:center;padding:18px}
      .elagili-notify-overlay.is-open{display:flex}
      .elagili-notify-modal{width:min(460px,100%);background:#fff;border-radius:18px;padding:22px;box-shadow:0 18px 60px rgba(0,0,0,.22);position:relative}
      .elagili-notify-close{position:absolute;top:10px;right:12px;border:0;background:transparent;color:#354557;font-size:30px;line-height:1;cursor:pointer}
      .elagili-notify-modal h3{margin:0 36px 8px 0;color:#263441}
      .elagili-notify-modal p{margin:0 0 14px;color:#66727d}
      .elagili-notify-form{display:grid;gap:10px}
      .elagili-notify-form input[type=email]{width:100%;min-height:46px;border:1px solid #cfd6dc;border-radius:10px;padding:10px 12px;font:inherit}
      .elagili-notify-submit{min-height:46px;border:0;border-radius:999px;background:#354557;color:#fff;padding:10px 16px;font:inherit;font-weight:700;cursor:pointer}
      .elagili-notify-submit:disabled{opacity:.6;cursor:wait}
      .elagili-notify-status{font-size:14px;min-height:20px}
      .elagili-notify-status.is-success{color:#19703b}
      .elagili-notify-status.is-error{color:#b42318}
      @media(max-width:749px){.elagili-notify-btn{min-height:34px;padding:6px 10px;font-size:12px}}
    `;
    document.head.appendChild(style);
  };

  const getCardProductData = (row) => {
    const card = row.closest('product-card');
    if (!card) return null;
    const link = card.querySelector('.product-card__link[href]');
    const title = card.querySelector('.visually-hidden')?.textContent?.trim() || 'Product';
    return {
      productId: String(card.dataset.productId || ''),
      variantId: String(row.dataset.variantId || ''),
      title,
      url: link?.href || window.location.href
    };
  };

  const getProductPageData = () => {
    const productForm = document.querySelector('main product-form-component[data-product-id]');
    const variantInput = productForm?.querySelector('input[name="id"]');
    const title = document.querySelector('meta[property="og:title"]')?.content || document.querySelector('main h1')?.textContent?.trim() || document.title;
    return {
      productId: String(productForm?.dataset?.productId || ''),
      variantId: String(variantInput?.value || ''),
      title,
      url: window.location.href
    };
  };

  const openModal = (data) => {
    const overlay = document.querySelector('.elagili-notify-overlay');
    if (!overlay) return;
    overlay.dataset.productId = data.productId || '';
    overlay.dataset.variantId = data.variantId || '';
    overlay.dataset.productTitle = data.title || '';
    overlay.dataset.productUrl = data.url || '';
    overlay.querySelector('.elagili-notify-product-name').textContent = data.title || 'this product';
    overlay.querySelector('.elagili-notify-status').textContent = '';
    overlay.querySelector('.elagili-notify-status').className = 'elagili-notify-status';
    overlay.querySelector('input[type=email]').value = '';
    overlay.classList.add('is-open');
    setTimeout(() => overlay.querySelector('input[type=email]')?.focus(), 0);
  };

  const createModal = () => {
    if (document.querySelector('.elagili-notify-overlay')) return;
    const overlay = document.createElement('div');
    overlay.className = 'elagili-notify-overlay';
    overlay.innerHTML = `
      <div class="elagili-notify-modal" role="dialog" aria-modal="true" aria-label="Back in stock notification">
        <button type="button" class="elagili-notify-close" aria-label="Close">×</button>
        <h3>Notify me when available</h3>
        <p>Enter your email and we’ll record your request for <strong class="elagili-notify-product-name"></strong>.</p>
        <form class="elagili-notify-form">
          <input type="email" name="email" placeholder="Email address" required autocomplete="email">
          <button type="submit" class="elagili-notify-submit">Notify me</button>
          <div class="elagili-notify-status" aria-live="polite"></div>
        </form>
      </div>`;
    document.body.appendChild(overlay);

    const close = () => overlay.classList.remove('is-open');
    overlay.querySelector('.elagili-notify-close').addEventListener('click', close);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) close(); });
    document.addEventListener('keydown', (event) => { if (event.key === 'Escape') close(); });

    overlay.querySelector('.elagili-notify-form').addEventListener('submit', async (event) => {
      event.preventDefault();
      const form = event.currentTarget;
      const email = form.email.value.trim();
      const submit = form.querySelector('.elagili-notify-submit');
      const status = form.querySelector('.elagili-notify-status');
      if (!email) return;

      submit.disabled = true;
      status.className = 'elagili-notify-status';
      status.textContent = 'Saving your request…';

      const body = new FormData();
      body.append('form_type', 'contact');
      body.append('utf8', '✓');
      body.append('contact[email]', email);
      body.append('contact[subject]', 'Back in stock request');
      body.append('contact[body]', `Back in stock request\nProduct: ${overlay.dataset.productTitle || ''}\nProduct ID: ${overlay.dataset.productId || ''}\nVariant ID: ${overlay.dataset.variantId || ''}\nURL: ${overlay.dataset.productUrl || ''}\nCustomer email: ${email}`);

      try {
const response = await fetch('/apps/elagili-tracking/back-in-stock', {
  method: 'POST',
  credentials: 'same-origin',
  headers: {
    'Content-Type': 'application/json',
    Accept: 'application/json'
  },
  body: JSON.stringify({
    email,
    product_id: overlay.dataset.productId || '',
    variant_id: overlay.dataset.variantId || '',
    product_title: overlay.dataset.productTitle || '',
    product_url: overlay.dataset.productUrl || ''
  })
});
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        status.className = 'elagili-notify-status is-success';
        status.textContent = 'Done — your request has been recorded.';
        submit.textContent = 'Recorded';
        setTimeout(() => { close(); submit.textContent = 'Notify me'; submit.disabled = false; }, 1400);
      } catch (error) {
        console.error('Elagili back in stock request failed', error);
        status.className = 'elagili-notify-status is-error';
        status.textContent = 'Could not save your request. Please try again.';
        submit.disabled = false;
      }
    });
  };

  const enhanceSoldOutCards = (root = document) => {
    root.querySelectorAll?.('.elagili-card-buy-row[data-available="false"]').forEach((row) => {
      const control = row.querySelector('.elagili-card-control');
      if (!control) return;
      if (control.querySelector('.elagili-notify-btn')) return;
      const data = getCardProductData(row);
      control.innerHTML = '';
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'elagili-notify-btn';
      button.textContent = 'Notify me';
      button.addEventListener('click', (event) => { event.preventDefault(); event.stopPropagation(); if (data) openModal(data); });
      control.appendChild(button);
    });
  };

  const getProductAddButton = () => {
  return (
    document.querySelector('main .buy-buttons-block button[type="submit"]') ||
    document.querySelector('main .buy-buttons-block .add-to-cart-button') ||
    document.querySelector('main product-form-component button[name="add"]') ||
    document.querySelector('main product-form button[name="add"]') ||
    document.querySelector('main form[action*="/cart/add"] button[type="submit"]') ||
    document.querySelector('main form[data-type="add-to-cart-form"] button[type="submit"]') ||
    document.querySelector('main button[id*="ProductSubmitButton"]') ||
    document.querySelector('main button[name="add"]')
  );
};

const productPageIsSoldOut = () => {
  if (!/\/products\//.test(window.location.pathname)) return false;

  // Use the selected variant's real availability, not 'Sold out' text
  // from other option buttons or transient disabled states.
  const selectedControl = document.querySelector(
    'main [data-elagili-product-cart-control][data-available]'
  );
  if (selectedControl) return selectedControl.dataset.available === 'false';

  // Fallback for templates without the Elagili variant cart control.
  const addButton = getProductAddButton();
  if (!addButton) return false;
  const text = (addButton.textContent || '').trim().toLowerCase();
  return (
    text.includes('sold out') ||
    text.includes('unavailable') ||
    addButton.disabled ||
    addButton.getAttribute('aria-disabled') === 'true'
  );
};

const enhanceProductPage = () => {
  if (!/\/products\//.test(window.location.pathname)) return;

  const existing = document.querySelector('.elagili-notify-btn--product');
  const soldOut = productPageIsSoldOut();

  if (!soldOut) {
    existing?.remove();
    return;
  }

  if (existing) return;

  const addButton = getProductAddButton();

  const productForm =
    document.querySelector('main product-form-component[data-product-id]') ||
    document.querySelector('main product-form-component') ||
    document.querySelector('main product-form') ||
    document.querySelector('main form[action*="/cart/add"]');

  const buyBlock =
    document.querySelector('main .buy-buttons-block') ||
    addButton?.parentElement ||
    productForm;

  if (!buyBlock) return;

  const button = document.createElement('button');

  button.type = 'button';
  button.className = 'elagili-notify-btn elagili-notify-btn--product';
  button.textContent = 'Notify me when available';

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    openModal(getProductPageData());
  });

  buyBlock.insertAdjacentElement('afterend', button);
};

  const run = (root = document) => {
    injectStyles();
    createModal();
    enhanceSoldOutCards(root);
    enhanceProductPage();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => run(document), { once: true });
  else run(document);

  document.addEventListener('shopify:section:load', (event) => run(event.target || document));
  document.addEventListener('change', () => setTimeout(() => enhanceProductPage(), 80), true);
  document.addEventListener('variant:update', () => setTimeout(enhanceProductPage, 0));

  let enhancementFrame = 0;
  const relevantSelector = '.elagili-card-buy-row, product-card, main product-form-component, main product-form, main .buy-buttons-block, main form[action*="/cart/add"]';
  const scheduleEnhancements = () => {
    if (enhancementFrame) return;
    enhancementFrame = requestAnimationFrame(() => {
      enhancementFrame = 0;
      enhanceSoldOutCards(document);
      enhanceProductPage();
    });
  };
  const observer = new MutationObserver((mutations) => {
    const relevant = mutations.some((mutation) => {
      if (mutation.type === 'attributes') {
        return !!mutation.target?.closest?.(relevantSelector);
      }
      return Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) return false;
        return node.matches?.(relevantSelector) || !!node.querySelector?.(relevantSelector);
      });
    });
    if (relevant) scheduleEnhancements();
  });
  if (document.querySelector('.elagili-card-buy-row') || /\/products\//.test(window.location.pathname)) {
    observer.observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['disabled', 'aria-disabled', 'data-available'] });
  }
})();


  (() => {
    if (window.__elagiliPackagingOptionsLoaded) return;
    window.__elagiliPackagingOptionsLoaded = true;

    const preferenceName = 'attributes[Packaging preference]';

    const getSections = () => {
      const ids = new Set();
      document.querySelectorAll('cart-items-component[data-section-id]').forEach((component) => {
        if (component.dataset.sectionId) ids.add(component.dataset.sectionId);
      });
      return Array.from(ids);
    };

    const feeId = 54725495882061;
    const root = window.Shopify?.routes?.root || '/';
    let tasks = Promise.resolve();
    const enqueue = (work) => {
      const task = tasks.then(work);
      tasks = task.catch(() => {});
      return task;
    };
    window.elagiliPackagingTask = enqueue;

    const readJSON = async (url, options = {}) => {
      const response = await fetch(url, {
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        ...options,
      });
      if (!response.ok) throw new Error('Could not update packaging');
      const data = await response.json();
      if (data.errors) throw new Error('Could not update packaging');
      return data;
    };
    const emit = (cart) => document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        data: { source: 'elagili-packaging-options', itemCount: cart.item_count, sections: cart.sections || {} },
        resource: cart,
      },
    }));
    const getRequired = (sections) => {
      for (const html of Object.values(sections || {})) {
        if (typeof html !== 'string') continue;
        const marker = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-packaging-required]');
        if (marker) return marker.dataset.packagingRequired === 'true';
      }
      return null;
    };
    const withSections = async (cart) => {
      if (getRequired(cart.sections) !== null || cart.items.length === 0) return cart;
      const url = new URL(window.location.href);
      url.searchParams.set('sections', getSections().join(','));
      cart.sections = await readJSON(url.toString());
      if (getRequired(cart.sections) === null) {
        const response = await fetch(root + 'cart', {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: { Accept: 'text/html' },
        });
        if (!response.ok) throw new Error('Could not refresh packaging eligibility');
        const html = await response.text();
        cart.__elagiliPackagingRequired = getRequired({ cart: html });
      }
      return cart;
    };
    const normalize = async (cart) => {
      cart = await withSections(cart);
      const required = cart.items.length === 0 ? false : (getRequired(cart.sections) ?? cart.__elagiliPackagingRequired ?? null);
      if (required === null) throw new Error('Could not check packaging eligibility');
      const fees = cart.items.filter((item) => Number(item.variant_id) === feeId);
      const preference = cart.attributes?.['Packaging preference'] || '';
      const updates = {};
      fees.forEach((item, index) => {
        const quantity = required && index === 0 ? 1 : 0;
        if (item.quantity !== quantity) updates[item.key] = quantity;
      });
      if (!Object.keys(updates).length && (required || !preference)) return cart;
      return readJSON(root + 'cart/update.js', {
        method: 'POST',
        body: JSON.stringify({
          updates,
          ...(!required ? { attributes: { 'Packaging preference': '' } } : {}),
          sections: getSections().join(','),
          sections_url: window.location.pathname,
        }),
      });
    };
    window.elagiliNormalizePackaging = normalize;

    let reconcileTimer;
    const scheduleReconcile = () => {
      clearTimeout(reconcileTimer);
      reconcileTimer = setTimeout(() => enqueue(async () => {
        const cart = await readJSON(root + 'cart.js');
        const normalized = await normalize(cart);
        if (normalized !== cart) emit(normalized);
      }).catch((error) => console.error('Elagili packaging reconciliation failed', error)), 0);
    };
    document.addEventListener('cart:update', (event) => {
      if (event.detail?.data?.source !== 'elagili-packaging-options') scheduleReconcile();
    });
    window.addEventListener('pageshow', scheduleReconcile);
    document.addEventListener('shopify:section:load', scheduleReconcile);

    const setBusy = (fieldset, busy) => {
      document.querySelectorAll('.elagili-packaging-options').forEach((group) => {
        group.classList.toggle('is-busy', busy);
        group.querySelectorAll('input[type="radio"]').forEach((input) => { input.disabled = busy; });
      });
      document.querySelectorAll('.cart__checkout-button').forEach((button) => {
        button.disabled = busy;
      });
    };

    const updatePackaging = async (input) => {
      const fieldset = input.closest('.elagili-packaging-options');
      if (!fieldset || document.querySelector('.elagili-packaging-options.is-busy')) return;

      const feeVariantId = Number(fieldset.dataset.feeVariantId);
      const choice = input.value;
      const wantsFee = choice === 'New Discreet Packaging';
      const status = fieldset.querySelector('.elagili-packaging-status');
      const sections = getSections();

      setBusy(fieldset, true);
      if (status) status.textContent = 'Updating your packaging choice…';

      try {
        await enqueue(async () => {
        let cartResponse = await fetch(root + 'cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (!cartResponse.ok) throw new Error('Could not read the cart');
        let cart = await withSections(await cartResponse.json());
        if ((getRequired(cart.sections) ?? cart.__elagiliPackagingRequired) !== true) {
          emit(await normalize(cart));
          return;
        }
        let feeLine = cart.items.find((item) => Number(item.variant_id) === feeVariantId);

        if (wantsFee && !feeLine) {
          const addResponse = await fetch(root + 'cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: [{ id: feeVariantId, quantity: 1 }] }),
          });
          if (!addResponse.ok) throw new Error('Could not add discreet packaging');
          cartResponse = await fetch(root + 'cart.js', {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
          });
          if (!cartResponse.ok) throw new Error('Could not refresh the cart');
          cart = await cartResponse.json();
          feeLine = cart.items.find((item) => Number(item.variant_id) === feeVariantId);
        }

        const updates = {};
        cart.items.filter((item) => Number(item.variant_id) === feeVariantId).forEach((item, index) => {
          updates[item.key] = wantsFee && index === 0 ? 1 : 0;
        });

        const updateResponse = await fetch(root + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            updates,
            attributes: { 'Packaging preference': choice },
            sections: sections.join(','),
            sections_url: window.location.pathname,
          }),
        });
        if (!updateResponse.ok) throw new Error('Could not update the packaging choice');

        const updatedCart = await normalize(await updateResponse.json());
        document.dispatchEvent(
          new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              data: {
                source: 'elagili-packaging-options',
                itemCount: updatedCart.item_count,
                sections: updatedCart.sections || {},
              },
              resource: updatedCart,
            },
          })
        );
        });
      } catch (error) {
        console.error('Elagili packaging update failed', error);
        if (status) status.textContent = 'Could not update your choice. Please try again.';
      } finally {
        setBusy(fieldset, false);
      }
    };

    const showPackagingTooltip = (info) => {
      const tooltip = info?.querySelector('.elagili-packaging-tooltip');
      if (!tooltip || typeof tooltip.showPopover !== 'function') return;
      if (!tooltip.matches(':popover-open')) tooltip.showPopover();

      const iconRect = info.getBoundingClientRect();
      const width = Math.min(270, window.innerWidth - 32);
      tooltip.style.width = `${width}px`;
      tooltip.style.left = `${Math.max(16, Math.min(window.innerWidth - width - 16, iconRect.left + iconRect.width / 2 - width / 2))}px`;
      tooltip.style.top = '0px';

      const tooltipHeight = tooltip.getBoundingClientRect().height;
      const above = iconRect.top - tooltipHeight - 8;
      tooltip.style.top = `${above >= 8 ? above : iconRect.bottom + 8}px`;
    };

    const hidePackagingTooltip = (info) => {
      const tooltip = info?.querySelector('.elagili-packaging-tooltip');
      if (tooltip?.matches(':popover-open')) tooltip.hidePopover();
    };

    document.addEventListener('pointerover', (event) => {
      const info = event.target.closest?.('.elagili-packaging-info');
      if (info) showPackagingTooltip(info);
    });

    document.addEventListener('pointerout', (event) => {
      const info = event.target.closest?.('.elagili-packaging-info');
      if (info && !info.contains(event.relatedTarget)) hidePackagingTooltip(info);
    });

    document.addEventListener('focusin', (event) => {
      const info = event.target.closest?.('.elagili-packaging-info');
      if (info) showPackagingTooltip(info);
    });

    document.addEventListener('focusout', (event) => {
      const info = event.target.closest?.('.elagili-packaging-info');
      if (info) hidePackagingTooltip(info);
    });

    document.addEventListener('change', (event) => {
      const input = event.target.closest?.(`input[type="radio"][name="${preferenceName}"]`);
      if (input) updatePackaging(input);
    });
  })();
